import React, { useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ConsentDetails, formatConsentId, formatRelativeTime, DataCluster } from '../../constants/uaeStandards';
import { ConsentStateBadge } from '../ui/ConsentStateBadge';
import { Button } from '../ui/Button';
import { getBankTheme } from '../../constants/bankThemes';
import { ArrowLeftIcon, CalendarIcon, ActivityIcon, CopyIcon, CheckIcon, ChevronDownIcon, AlertTriangleIcon, XCircleIcon, CheckCircle2Icon, ShieldAlertIcon, BuildingIcon, InfoIcon, UserIcon, DollarSignIcon, CreditCardIcon, UsersIcon, FileTextIcon } from 'lucide-react';
interface LFIConsentDetailedViewProps {
  consent: ConsentDetails;
  bankId: string;
  bankName: string;
  onBack: () => void;
  onAuthorize?: () => void;
  onReject?: () => void;
  onRevoke?: () => void;
  onSuspend?: () => void;
}
// Icon mapping for data clusters - VeloX branded
const dataClusterIconMap: Record<string, ComponentType<any>> = {
  User: UserIcon,
  DollarSign: DollarSignIcon,
  CreditCard: CreditCardIcon,
  Info: InfoIcon,
  Users: UsersIcon,
  FileText: FileTextIcon
};
// Helper to get icon component for a data cluster
function getIconComponent(iconName?: string): ComponentType<any> {
  if (!iconName) return FileTextIcon;
  return dataClusterIconMap[iconName] || FileTextIcon;
}
export function LFIConsentDetailedView({
  consent,
  bankId,
  bankName,
  onBack,
  onAuthorize,
  onReject,
  onRevoke,
  onSuspend
}: LFIConsentDetailedViewProps) {
  const [expandedClusters, setExpandedClusters] = useState<string[]>([]);
  const [copiedId, setCopiedId] = useState(false);
  const bankTheme = getBankTheme(bankId) || {
    colors: {
      primary: '#1e3a5f',
      primaryDark: '#152b47',
      primaryLight: '#2a4a75'
    },
    gradients: {
      header: 'linear-gradient(135deg, #1e3a5f 0%, #152b47 100%)',
      button: 'linear-gradient(135deg, #1e3a5f 0%, #2a4a75 100%)'
    },
    fonts: {
      heading: 'system-ui, -apple-system, sans-serif',
      body: 'system-ui, -apple-system, sans-serif'
    },
    borderRadius: '12px'
  };
  const canAuthorize = consent.state === 'Awaiting' && onAuthorize;
  const canReject = consent.state === 'Awaiting' && onReject;
  const canRevoke = ['Authorized', 'Suspended'].includes(consent.state) && onRevoke;
  const canSuspend = consent.state === 'Authorized' && onSuspend;
  const toggleCluster = (cluster: string) => {
    setExpandedClusters(prev => prev.includes(cluster) ? prev.filter(c => c !== cluster) : [...prev, cluster]);
  };
  const copyConsentId = () => {
    navigator.clipboard.writeText(consent.consentId);
    setCopiedId(true);
    setTimeout(() => setCopiedId(false), 2000);
  };
  return <div className="space-y-6" data-id="element-867">
      {/* Header - Removed back button since it's now in CMITopBar */}
      <div data-id="element-868">
        <div className="flex items-start justify-between" data-id="element-869">
          <div className="flex items-center gap-4" data-id="element-870">
            <div className="w-16 h-16 flex-shrink-0 flex items-center justify-center p-3 bg-gray-50" style={{
            borderRadius: bankTheme.borderRadius
          }} data-id="element-871">
              <img src={consent.tppLogo} alt={consent.tppTradingName} className="w-full h-full object-contain" data-id="element-872" />
            </div>
            <div data-id="element-873">
              <h1 className="text-2xl font-bold mb-1" style={{
              fontFamily: bankTheme.fonts.heading,
              color: bankTheme.colors.primaryDark
            }} data-id="element-874">
                {consent.tppTradingName}
              </h1>
              <div className="text-sm mb-1" style={{
              color: '#666666'
            }} data-id="element-875">
                {consent.merchantName && `${consent.merchantName} via `}
                {consent.type === 'DataSharing' ? 'Data Sharing' : 'Service Initiation'}
              </div>
              {consent.tppLegalName !== consent.tppTradingName && <div className="text-xs" style={{
              color: '#9CA3AF'
            }} data-id="element-876">
                  Legal entity: {consent.tppLegalName}
                </div>}
            </div>
          </div>
          <ConsentStateBadge state={consent.state} size="lg" data-id="element-877" />
        </div>
      </div>

      {/* Pending Authorization Notice */}
      {consent.state === 'Awaiting' && <motion.div initial={{
      opacity: 0,
      y: -10
    }} animate={{
      opacity: 1,
      y: 0
    }} className="flex items-start gap-3 p-4 rounded-xl" style={{
      backgroundColor: '#FFF3E0',
      border: '2px solid #FF9800'
    }} data-id="element-878">
          <AlertTriangleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{
        color: '#E65100'
      }} data-id="element-879" />
          <div data-id="element-880">
            <div className="font-semibold text-sm mb-1" style={{
          color: '#E65100'
        }} data-id="element-881">
              Authorization Required
            </div>
            <div className="text-xs" style={{
          color: '#E65100'
        }} data-id="element-882">
              This TPP is requesting access to your account. Review the details
              below and authorize or reject this request.
            </div>
          </div>
        </motion.div>}

      {/* Suspended Notice */}
      {consent.state === 'Suspended' && <motion.div initial={{
      opacity: 0,
      y: -10
    }} animate={{
      opacity: 1,
      y: 0
    }} className="flex items-start gap-3 p-4 rounded-xl" style={{
      backgroundColor: '#FFF3E0',
      border: '2px solid #FF9800'
    }} data-id="element-883">
          <ShieldAlertIcon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{
        color: '#E65100'
      }} data-id="element-884" />
          <div data-id="element-885">
            <div className="font-semibold text-sm mb-1" style={{
          color: '#E65100'
        }} data-id="element-886">
              Consent Suspended
            </div>
            <div className="text-xs" style={{
          color: '#E65100'
        }} data-id="element-887">
              This consent has been suspended by {bankName} due to suspected
              fraudulent activity. Contact us for more information.
            </div>
          </div>
        </motion.div>}

      {/* TPP Information Card */}
      <div className="p-6 border-2" style={{
      backgroundColor: '#FFFFFF',
      borderColor: '#E5E7EB',
      borderRadius: bankTheme.borderRadius
    }} data-id="element-888">
        <div className="text-sm font-semibold uppercase tracking-wide mb-4" style={{
        color: bankTheme.colors.primary
      }} data-id="element-889">
          Service Provider Details
        </div>

        <div className="space-y-4" data-id="element-890">
          {/* Consent ID */}
          <div className="flex items-center justify-between" data-id="element-891">
            <div data-id="element-892">
              <div className="text-xs font-medium mb-1" style={{
              color: '#9CA3AF'
            }} data-id="element-893">
                Consent ID
              </div>
              <div className="font-mono text-sm font-medium" style={{
              color: '#1A1A1A'
            }} data-id="element-894">
                {formatConsentId(consent.consentId)}
              </div>
            </div>
            <button onClick={copyConsentId} className="p-2 transition-colors rounded-lg hover:bg-gray-100" style={{
            color: bankTheme.colors.primary
          }} data-id="element-895">
              {copiedId ? <CheckIcon className="w-5 h-5" data-id="element-896" /> : <CopyIcon className="w-5 h-5" data-id="element-897" />}
            </button>
          </div>

          {/* TPP Details */}
          <div className="grid grid-cols-2 gap-4" data-id="element-898">
            <div data-id="element-899">
              <div className="text-xs font-medium mb-1" style={{
              color: '#9CA3AF'
            }} data-id="element-900">
                Trading Name
              </div>
              <div className="text-sm font-semibold" style={{
              color: '#1A1A1A'
            }} data-id="element-901">
                {consent.tppTradingName}
              </div>
            </div>

            <div data-id="element-902">
              <div className="text-xs font-medium mb-1" style={{
              color: '#9CA3AF'
            }} data-id="element-903">
                Legal Entity
              </div>
              <div className="text-sm font-semibold" style={{
              color: '#1A1A1A'
            }} data-id="element-904">
                {consent.tppLegalName}
              </div>
            </div>
          </div>

          {/* Account Details */}
          <div data-id="element-905">
            <div className="text-xs font-medium mb-1" style={{
            color: '#9CA3AF'
          }} data-id="element-906">
              Account Being Accessed
            </div>
            <div className="text-sm font-semibold" style={{
            color: '#1A1A1A'
          }} data-id="element-907">
              {consent.accountType}
            </div>
            <div className="text-xs font-mono mt-0.5" style={{
            color: '#666666'
          }} data-id="element-908">
              ****{consent.accountLastFour}
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4" data-id="element-909">
            <div data-id="element-910">
              <div className="text-xs font-medium mb-1" style={{
              color: '#9CA3AF'
            }} data-id="element-911">
                {consent.state === 'Awaiting' ? 'Requested Date' : 'Connected Date'}
              </div>
              <div className="flex items-center gap-2" data-id="element-912">
                <CalendarIcon className="w-4 h-4" style={{
                color: '#666666'
              }} data-id="element-913" />
                <span className="text-sm font-medium" style={{
                color: '#1A1A1A'
              }} data-id="element-914">
                  {consent.connectedDate}
                </span>
              </div>
            </div>

            <div data-id="element-915">
              <div className="text-xs font-medium mb-1" style={{
              color: '#9CA3AF'
            }} data-id="element-916">
                Expiry Date
              </div>
              <div className="flex items-center gap-2" data-id="element-917">
                <CalendarIcon className="w-4 h-4" style={{
                color: '#666666'
              }} data-id="element-918" />
                <span className="text-sm font-medium" style={{
                color: '#1A1A1A'
              }} data-id="element-919">
                  {consent.expiryDate}
                </span>
              </div>
            </div>
          </div>

          {/* Last Access */}
          {consent.lastAccessDate && consent.state === 'Authorized' && <div data-id="element-920">
              <div className="text-xs font-medium mb-1" style={{
            color: '#9CA3AF'
          }} data-id="element-921">
                Last Access by TPP
              </div>
              <div className="flex items-center gap-2" data-id="element-922">
                <ActivityIcon className="w-4 h-4" style={{
              color: '#666666'
            }} data-id="element-923" />
                <span className="text-sm font-medium" style={{
              color: '#1A1A1A'
            }} data-id="element-924">
                  {formatRelativeTime(consent.lastAccessDate)}
                </span>
              </div>
            </div>}
        </div>
      </div>

      {/* Data Access Permissions with VeloX branded icons */}
      <div data-id="element-925">
        <div className="text-sm font-semibold uppercase tracking-wide mb-3" style={{
        color: '#1A1A1A'
      }} data-id="element-926">
          What This TPP Can Access
        </div>

        <div className="space-y-3" data-id="element-927">
          {consent.dataClusters.map(cluster => {
          const isExpanded = expandedClusters.includes(cluster.cluster);
          const IconComponent = getIconComponent(cluster.icon);
          return <div key={cluster.cluster} className="border-2 overflow-hidden" style={{
            borderColor: '#E5E7EB',
            borderRadius: bankTheme.borderRadius,
            backgroundColor: '#FFFFFF'
          }} data-id="element-928">
                <button onClick={() => toggleCluster(cluster.cluster)} className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors" data-id="element-929">
                  <div className="flex items-center gap-3 text-left" data-id="element-930">
                    <div className="w-10 h-10 flex items-center justify-center flex-shrink-0" style={{
                  backgroundColor: bankTheme.colors.primary + '15',
                  borderRadius: '8px'
                }} data-id="element-931">
                      <IconComponent className="w-5 h-5" style={{
                    color: bankTheme.colors.primary
                  }} data-id="element-932" />
                    </div>
                    <div data-id="element-933">
                      <div className="font-semibold text-sm" style={{
                    color: '#1A1A1A'
                  }} data-id="element-934">
                        {cluster.cluster}
                      </div>
                      <div className="text-xs mt-0.5" style={{
                    color: '#9CA3AF'
                  }} data-id="element-935">
                        {cluster.permissions.length} permission
                        {cluster.permissions.length > 1 ? 's' : ''}
                        {cluster.lastAccessed && ` • Last accessed ${formatRelativeTime(cluster.lastAccessed)}`}
                      </div>
                    </div>
                  </div>
                  <motion.div animate={{
                rotate: isExpanded ? 180 : 0
              }} transition={{
                duration: 0.2
              }} data-id="element-936">
                    <ChevronDownIcon className="w-5 h-5" style={{
                  color: '#9CA3AF'
                }} data-id="element-937" />
                  </motion.div>
                </button>

                <AnimatePresence data-id="element-938">
                  {isExpanded && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.2
              }} className="overflow-hidden" data-id="element-939">
                      <div className="border-t-2 p-4 space-y-3" style={{
                  borderColor: '#F3F4F6',
                  backgroundColor: bankTheme.colors.primary + '05'
                }} data-id="element-940">
                        {cluster.permissions.map(permission => <div key={permission.id} data-id="element-941">
                            <div className="text-sm font-medium mb-1" style={{
                      color: '#1A1A1A'
                    }} data-id="element-942">
                              {permission.language}
                            </div>
                            <div className="text-xs" style={{
                      color: '#666666'
                    }} data-id="element-943">
                              Examples: {permission.examples}
                            </div>
                          </div>)}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>;
        })}
        </div>
      </div>

      {/* Actions */}
      <div className="space-y-3 pt-4 border-t-2" style={{
      borderColor: '#F3F4F6'
    }} data-id="element-944">
        {canAuthorize && <button onClick={onAuthorize} className="w-full px-4 py-3 font-semibold text-white transition-all shadow-md hover:shadow-lg active:scale-[0.98] flex items-center justify-center gap-2" style={{
        background: bankTheme.gradients.button,
        borderRadius: bankTheme.borderRadius
      }} data-id="element-945">
            <CheckCircle2Icon className="w-5 h-5" data-id="element-946" />
            <span data-id="element-947">Authorize This Consent</span>
          </button>}

        {canReject && <button onClick={onReject} className="w-full px-4 py-3 font-medium transition-all border-2 hover:bg-gray-50 active:scale-[0.98] flex items-center justify-center gap-2" style={{
        borderColor: '#E5E7EB',
        color: '#666666',
        borderRadius: bankTheme.borderRadius
      }} data-id="element-948">
            <XCircleIcon className="w-5 h-5" data-id="element-949" />
            <span data-id="element-950">Reject This Request</span>
          </button>}

        {canSuspend && <button onClick={onSuspend} className="w-full px-4 py-3 font-medium transition-all border-2 hover:bg-orange-50 active:scale-[0.98] flex items-center justify-center gap-2" style={{
        borderColor: '#FF9800',
        color: '#E65100',
        borderRadius: bankTheme.borderRadius
      }} data-id="element-951">
            <ShieldAlertIcon className="w-5 h-5" data-id="element-952" />
            <span data-id="element-953">Suspend Consent (Fraud Concern)</span>
          </button>}

        {canRevoke && <button onClick={onRevoke} className="w-full px-4 py-3 font-medium transition-all border-2 hover:bg-red-50 active:scale-[0.98] flex items-center justify-center gap-2" style={{
        borderColor: '#F44336',
        color: '#C62828',
        borderRadius: bankTheme.borderRadius
      }} data-id="element-954">
            <XCircleIcon className="w-5 h-5" data-id="element-955" />
            <span data-id="element-956">Revoke TPP Access</span>
          </button>}
      </div>

      {/* Contact TPP Notice */}
      {consent.state !== 'Awaiting' && <div className="p-4 rounded-xl" style={{
      backgroundColor: bankTheme.colors.primary + '10',
      border: `2px solid ${bankTheme.colors.primary}30`
    }} data-id="element-957">
          <div className="flex items-start gap-3" data-id="element-958">
            <InfoIcon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{
          color: bankTheme.colors.primary
        }} data-id="element-959" />
            <div data-id="element-960">
              <div className="text-xs font-semibold mb-1" style={{
            color: bankTheme.colors.primaryDark
          }} data-id="element-961">
                Important Notice
              </div>
              <div className="text-xs leading-relaxed" style={{
            color: bankTheme.colors.primaryDark
          }} data-id="element-962">
                If you revoke this consent, you should contact{' '}
                {consent.tppTradingName} to understand how this affects their
                services. Revoking consent will immediately stop them from
                accessing your data.
              </div>
            </div>
          </div>
        </div>}
    </div>;
}